import { ReactNode, useMemo } from "react";
import { Controller, FieldError, get, useFormContext, useFormState, useWatch } from "react-hook-form";
import { StatusSwitch } from "@/app/components/routes/auth_routes/QuickCommerce/HomeTileConfig/controls";
import { textInputClassName } from "@/app/components/routes/auth_routes/QuickCommerce/HomeTileConfig/interface";
import { SelectMenuItems } from "@/app/components/ui/interface";
import { GIFT_ID_CONDITIONS, GiftIdConditionKey, IGiftFormFields, toSelectItems } from "../interface";
import {
  ChipApiSearchSelect,
  ChipSearchSelect,
  ChipTextInput,
  FormField,
  GiftSelectField,
  SectionCard,
  SwitchField,
  errorBorderClassName,
} from "./fields";
import { validateNonNegativeNumber } from "./validators";
import { useGiftProductSearch } from "./useGiftProductSearch";

/** Bordered sub-section with its own on/off switch; the body only renders while it is on. */
const ConditionPanel = ({
  title,
  description,
  enabled,
  onToggle,
  children,
}: {
  title: string;
  description: string;
  enabled: boolean;
  onToggle: (next: boolean) => void;
  children: ReactNode;
}) => (
  <div className={`rounded-lg border ${enabled ? "border-[#193A6A]/25 bg-white" : "border-slate-200 bg-slate-50/60"}`}>
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="text-xs font-semibold text-slate-800">{title}</p>
        <p className="text-[11px] text-slate-500">{description}</p>
      </div>
      <StatusSwitch checked={enabled} onRequestChange={onToggle} label={`${title} condition`} />
    </div>
    {enabled && <div className="border-t border-slate-100 px-4 py-4">{children}</div>}
  </div>
);

const IdConditionPanel = ({
  conditionKey,
  title,
  description,
  itemLabel,
  placeholder,
  operationItems,
  isFormatLoading,
  optionItems,
  isOptionsLoading = false,
  search,
}: {
  conditionKey: GiftIdConditionKey;
  title: string;
  description: string;
  itemLabel: string;
  placeholder: string;
  operationItems: SelectMenuItems;
  isFormatLoading: boolean;
  /** When given, values are picked from this list (searchable) instead of typed freely. */
  optionItems?: SelectMenuItems;
  isOptionsLoading?: boolean;
  /** When given, values are found through this search API as the user types. */
  search?: (query: string) => Promise<SelectMenuItems>;
}) => {
  const { control, getValues, register, setValue, trigger } = useFormContext<IGiftFormFields>();
  const idNames = useWatch({ control, name: "id_names" });
  const { errors } = useFormState({ control, name: `eligibility.${conditionKey}` });
  const base = `eligibility.${conditionKey}` as const;
  const [enabled, includedIds, excludedIds] = useWatch({
    control,
    name: [`${base}.enabled`, `${base}.included_ids`, `${base}.excluded_ids`],
  });
  const includedError: FieldError | undefined = get(errors, `${base}.included_ids`);
  const minIdsError: FieldError | undefined = get(errors, `${base}.min_ids_present`);
  const lowerLabel = itemLabel.toLowerCase();

  // Rules apply only while this condition is switched on.
  const isActive = (formValues: IGiftFormFields) => formValues.eligibility[conditionKey].enabled;

  // Remember the name of a picked clinic/product so the payload can send {id, name}.
  const rememberName = (item: { id: string; title: string }) =>
    setValue("id_names", { ...getValues("id_names"), [item.id]: item.title });

  const renderPicker = ({
    fieldId,
    value,
    onChange,
    blockedValues,
    blockedMessage,
    hasError = false,
  }: {
    fieldId: string;
    value: string[];
    onChange: (next: string[]) => void;
    blockedValues: string[];
    blockedMessage: string;
    hasError?: boolean;
  }) => {
    if (search) {
      return (
        <ChipApiSearchSelect
          id={fieldId}
          value={value}
          onChange={onChange}
          search={search}
          blockedValues={blockedValues}
          names={idNames}
          onPick={rememberName}
          placeholder={placeholder}
          hasError={hasError}
        />
      );
    }
    if (optionItems) {
      return (
        <ChipSearchSelect
          id={fieldId}
          items={optionItems}
          value={value}
          onChange={onChange}
          blockedValues={blockedValues}
          names={idNames}
          onPick={rememberName}
          placeholder={placeholder}
          isLoading={isOptionsLoading}
          hasError={hasError}
        />
      );
    }
    return (
      <ChipTextInput
        id={fieldId}
        value={value}
        onChange={onChange}
        blockedValues={blockedValues}
        blockedMessage={blockedMessage}
        getLabel={(item) => idNames[item] || item}
        placeholder={placeholder}
        hasError={hasError}
      />
    );
  };

  return (
    <ConditionPanel
      title={title}
      description={description}
      enabled={enabled}
      onToggle={(next) => setValue(`${base}.enabled`, next, { shouldDirty: true })}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField label={`Included ${itemLabel}`} error={includedError?.message}>
          <Controller
            control={control}
            name={`${base}.included_ids`}
            rules={{
              validate: (value, formValues) => {
                if (!isActive(formValues)) return true;
                const excluded = formValues.eligibility[conditionKey].excluded_ids;
                if (!value.length && !excluded.length) return `Add at least one included or excluded ${lowerLabel}.`;
                return value.every((id) => !excluded.includes(id)) || `Cannot be both included and excluded.`;
              },
            }}
            render={({ field }) =>
              renderPicker({
                fieldId: `${base}.included_ids`,
                value: field.value,
                onChange: field.onChange,
                blockedValues: excludedIds,
                blockedMessage: `already in excluded ${lowerLabel}.`,
                hasError: Boolean(includedError),
              })
            }
          />
        </FormField>
        <FormField label={`Excluded ${itemLabel}`}>
          <Controller
            control={control}
            name={`${base}.excluded_ids`}
            render={({ field }) =>
              renderPicker({
                fieldId: `${base}.excluded_ids`,
                value: field.value,
                onChange: (next) => {
                  field.onChange(next);
                  // Adding an excluded id can satisfy the "at least one" rule shown on the included field.
                  if (includedError) trigger(`${base}.included_ids`);
                },
                blockedValues: includedIds,
                blockedMessage: `already in included ${lowerLabel}.`,
              })
            }
          />
        </FormField>
        <GiftSelectField
          name={`${base}.operation`}
          label={`Included ${itemLabel} Match`}
          hint="Any: one of the included values is enough. All: every included value is needed."
          items={operationItems}
          isLoading={isFormatLoading}
          placeholder="Select match"
        />
        <FormField
          label={`Min ${itemLabel} Present`}
          htmlFor={`${base}.min_ids_present`}
          error={minIdsError?.message}
          hint="0 means no minimum."
        >
          <input
            id={`${base}.min_ids_present`}
            type="number"
            min={0}
            className={`${textInputClassName} ${errorBorderClassName(Boolean(minIdsError))}`}
            {...register(`${base}.min_ids_present`, {
              validate: (value, formValues) => {
                if (!isActive(formValues)) return true;
                const result = validateNonNegativeNumber(`Min ${lowerLabel} present`)(value);
                if (result !== true) return result;
                if (!Number.isInteger(Number(value || 0))) return "Must be a whole number.";
                return (
                  Number(value || 0) <= formValues.eligibility[conditionKey].included_ids.length ||
                  `Cannot be more than the number of included ${lowerLabel}.`
                );
              },
            })}
          />
        </FormField>
      </div>
    </ConditionPanel>
  );
};

const CartConditionPanel = () => {
  const { control, register, setValue } = useFormContext<IGiftFormFields>();
  const { errors } = useFormState({ control, name: "eligibility.cart" });
  const enabled = useWatch({ control, name: "eligibility.cart.enabled" });
  const cartErrors = errors.eligibility?.cart;

  const isActive = (formValues: IGiftFormFields) => formValues.eligibility.cart.enabled;

  return (
    <ConditionPanel
      title="Cart Conditions"
      description="Cart total the gift unlocks at."
      enabled={enabled}
      onToggle={(next) => setValue("eligibility.cart.enabled", next, { shouldDirty: true })}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField
          label="Min Cart Value (₹)"
          htmlFor="eligibility.cart.min_value"
          error={cartErrors?.min_value?.message}
        >
          <input
            id="eligibility.cart.min_value"
            type="number"
            min={0}
            step="0.01"
            placeholder="No minimum"
            className={`${textInputClassName} ${errorBorderClassName(Boolean(cartErrors?.min_value))}`}
            {...register("eligibility.cart.min_value", {
              validate: (value, formValues) => {
                if (!isActive(formValues)) return true;
                const result = validateNonNegativeNumber("Min cart value")(value);
                if (result !== true) return result;
                return (
                  Boolean(value.trim() || formValues.eligibility.cart.max_value.trim()) ||
                  "Enter a min or max cart value."
                );
              },
              deps: ["eligibility.cart.max_value"],
            })}
          />
        </FormField>
        <FormField
          label="Max Cart Value (₹)"
          htmlFor="eligibility.cart.max_value"
          error={cartErrors?.max_value?.message}
        >
          <input
            id="eligibility.cart.max_value"
            type="number"
            min={0}
            step="0.01"
            placeholder="No maximum"
            className={`${textInputClassName} ${errorBorderClassName(Boolean(cartErrors?.max_value))}`}
            {...register("eligibility.cart.max_value", {
              validate: (value, formValues) => {
                if (!isActive(formValues)) return true;
                const result = validateNonNegativeNumber("Max cart value")(value);
                if (result !== true) return result;
                const minValue = formValues.eligibility.cart.min_value;
                return (
                  !value.trim() ||
                  !minValue.trim() ||
                  Number(value) >= Number(minValue) ||
                  "Max cart value must be at least the min cart value."
                );
              },
              deps: ["eligibility.cart.min_value"],
            })}
          />
        </FormField>
      </div>
    </ConditionPanel>
  );
};

const EventConditionPanel = () => {
  const { control, register, setValue } = useFormContext<IGiftFormFields>();
  const { errors } = useFormState({ control, name: "eligibility.event" });
  const [enabled, isEventTypeGift] = useWatch({
    control,
    name: ["eligibility.event.enabled", "eligibility.event.is_event_type_gift"],
  });
  const conditionNameError = errors.eligibility?.event?.condition_name;

  return (
    <ConditionPanel
      title="Event Conditions"
      description="Gift tied to an app event."
      enabled={enabled}
      onToggle={(next) => setValue("eligibility.event.enabled", next, { shouldDirty: true })}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <SwitchField
            label="Event type gift"
            hint="The gift is given only when the event below happens."
            checked={isEventTypeGift}
            onChange={(next) =>
              setValue("eligibility.event.is_event_type_gift", next, { shouldDirty: true, shouldValidate: false })
            }
          />
        </div>
        <FormField
          label="Condition Name"
          htmlFor="eligibility.event.condition_name"
          required={isEventTypeGift}
          error={conditionNameError?.message}
        >
          <input
            id="eligibility.event.condition_name"
            type="text"
            placeholder="Enter condition name"
            className={`${textInputClassName} ${errorBorderClassName(Boolean(conditionNameError))}`}
            {...register("eligibility.event.condition_name", {
              // The backend rejects an event type gift without a condition name.
              validate: (value, formValues) =>
                !formValues.eligibility.event.enabled ||
                !formValues.eligibility.event.is_event_type_gift ||
                Boolean(value.trim()) ||
                "Condition name is required for an event type gift.",
            })}
          />
        </FormField>
        <FormField label="Conditions">
          <Controller
            control={control}
            name="eligibility.event.conditions"
            render={({ field }) => (
              <ChipTextInput
                id="eligibility.event.conditions"
                value={field.value}
                onChange={field.onChange}
                placeholder="Type a condition and press Enter"
              />
            )}
          />
        </FormField>
      </div>
    </ConditionPanel>
  );
};

export const EligibilityCard = ({
  allowedOperations,
  isFormatLoading,
  clinicItems,
  isClinicListLoading,
}: {
  allowedOperations: string[];
  isFormatLoading: boolean;
  clinicItems: SelectMenuItems;
  isClinicListLoading: boolean;
}) => {
  const searchProductItems = useGiftProductSearch();

  // Every include/exclude condition offers the same AND/OR values from the `format` action.
  const operationItems = useMemo(() => toSelectItems(allowedOperations), [allowedOperations]);

  return (
    <SectionCard
      title="Eligibility Conditions"
      description="Who can get this gift. Switch on only the conditions you need; the rest are not checked."
    >
      <div className="space-y-3">
        {GIFT_ID_CONDITIONS.map((condition) => (
          <IdConditionPanel
            key={condition.key}
            conditionKey={condition.key}
            title={condition.title}
            description={condition.description}
            itemLabel={condition.itemLabel}
            placeholder={condition.placeholder}
            operationItems={operationItems}
            isFormatLoading={isFormatLoading}
            {...(condition.key === "clinic" && { optionItems: clinicItems, isOptionsLoading: isClinicListLoading })}
            {...(condition.key === "product" && { search: searchProductItems })}
          />
        ))}
        <CartConditionPanel />
        <EventConditionPanel />
      </div>
    </SectionCard>
  );
};
